import { ArrowBack } from '@mui/icons-material'
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography
} from '@mui/material'
import { FC, useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import DviPage from '../../components/DviPage/DviPage'
import { ConcernAndNotes } from '../../components/Estimates/ConcernAndNotes/ConcernAndNotes'
import RepairOrderMediaButton from '../../components/Estimates/RepairOrderMedia/RepairOrderMediaButton'
import { ROStatusSelect } from '../../components/Estimates/ROStatusSelect'
import { ServiceCard } from '../../components/Estimates/ServiceCard'
import DviMessage from '../../components/Inspections/DviMessage'
import DviVehicleUpdateButton from '../../components/Inspections/DviVehicleUpdateButton'
import InspectionsPanel from '../../components/InspectionsPanel/InspectionsPanel'
import { ClockInForRepairOrderDialog } from '../../components/TimeClockCard/ClockInForRepairOrderDialog'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NewServiceStatus } from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import { AppRoutes } from '../../models'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { RepairOrderContext } from '../../providers/RepairOrderProvider/RepairOrderProvider'
import { useGetUsersWithPermissionQuery } from '../../redux/api'
import { useGenerateRepairOrderCustomerSummaryMutation } from '../../redux/api/independentApi'
import {
  GetUsersWithPermissionActEnum,
  ServiceInfoStatusEnum,
  ShopInfo
} from '../../sdk'
import {
  estimateServiceCardStyle,
  repairOrderServiceCardStyle
} from '../../styles/serviceCard'
import { defaultTimeZoneDateToUtcDate } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'

const DviSingleJob: FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [isTimeClockChecked, setIsTimeClockChecked] = useState(false)
  const { shopProfile } = useContext(AppContext)

  const [statusFilterOption, setStatusFilterOption] = useState<
    NewServiceStatus | 'All'
  >(ServiceInfoStatusEnum.Authorized)

  const {
    repairOrder,
    vehicle,
    customer,
    services,
    transformedServiceItems,
    inspections,
    isLoading
  } = useContext(RepairOrderContext)
  const filteredServiceItems = useMemo(() => {
    if (statusFilterOption === 'All') {
      return transformedServiceItems
    } else {
      return transformedServiceItems?.filter(
        ({ status }) => status === statusFilterOption
      )
    }
  }, [statusFilterOption, transformedServiceItems])

  const { accessToken, shopId, userId } = useAccessTokenAndShopId()
  const { data: allowedUsers } = useGetUsersWithPermissionQuery(
    {
      authorization: accessToken,
      shop: shopId,
      act: GetUsersWithPermissionActEnum.ManageRepairOrder
    },
    { pollingInterval: 60000 }
  )
  const canEditStatus = useMemo(
    () => !!allowedUsers?.find((u) => u.id === userId),
    [allowedUsers, userId]
  )
  const [generateRepairOrderCustomerSummary] =
    useGenerateRepairOrderCustomerSummaryMutation()

  const generateRoSummary = useCallback(async () => {
    if (!repairOrder || !services || !customer || !shopProfile) {
      return ''
    }
    return await generateRepairOrderCustomerSummary({
      authorization: accessToken,
      repairOrderCustomerSummaryRequest: {
        repairOrderExpanded: repairOrder,
        services: services.filter(
          (s) => s.entry!.info!.status !== ServiceInfoStatusEnum.Deferred
        ),
        shopEntry: {
          ...shopProfile,
          info: {
            ...shopProfile.info,
            // sdk added time zone offset to the original time, need to convert it back to utc to avoid backend validation error
            calendarStartTime: defaultTimeZoneDateToUtcDate(
              new Date(shopProfile.info!.calendarStartTime!)
            ),
            calendarEndTime: defaultTimeZoneDateToUtcDate(
              new Date(shopProfile.info!.calendarEndTime!)
            )
          } as ShopInfo
        },
        customerEntry: customer
      }
    }).unwrap()
  }, [
    accessToken,
    customer,
    generateRepairOrderCustomerSummary,
    repairOrder,
    services,
    shopProfile
  ])

  return (
    <DviPage
      title="Job"
      actionsRight={
        <IconButton
          onClick={() => navigate(AppRoutes.DVI_JOBS)}
          sx={{ color: 'primary.light' }}>
          <ArrowBack />
        </IconButton>
      }
      isLoading={isLoading}>
      {!isTimeClockChecked && (
        <ClockInForRepairOrderDialog
          setIsTimeClockChecked={setIsTimeClockChecked}
          repairOrder={id!}
        />
      )}
      {repairOrder && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            justifyContent={'space-between'}>
            <Stack>
              <Typography variant="h2">
                {`RO#${repairOrder?.entry?.localId}`}
              </Typography>
              <Typography variant="h6">
                {`${vehicle?.info?.year} ${vehicle?.info?.make?.name} ${vehicle?.info?.model?.name}`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <RepairOrderMediaButton
                repairOrderId={repairOrder.entry!.id!}
                iconOnly
              />
              {canEditStatus && (
                <ROStatusSelect repairOrder={repairOrder} disableClosure />
              )}
            </Stack>
          </Stack>

          <Grid container>
            <Grid item xs={4}>
              <Stack>
                <Typography variant="subtitle1">Customer:</Typography>
                <Typography>{formatName(customer?.information)}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={8}>
              <Stack direction="row" justifyContent={'space-between'}>
                <Stack>
                  <Typography variant="subtitle1">Created On:</Typography>
                  <Typography>{`${repairOrder.entry?.created?.toLocaleDateString()}`}</Typography>
                </Stack>
                <Box>
                  <DviMessage
                    repairOrderId={repairOrder.entry!.id!}
                    repairOrderNumber={repairOrder.entry!.localId!}
                    customer={customer}
                    generateRoSummary={generateRoSummary}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Stack>
                <Typography variant="subtitle1">Plate:</Typography>
                <Typography>
                  {vehicle?.info!.licensePlate
                    ? vehicle.info!.licensePlate
                    : 'Not Set'}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={8}>
              <Stack direction="row" justifyContent={'space-between'}>
                <Stack>
                  <Typography variant="subtitle1">Mileage:</Typography>
                  <Typography>
                    {vehicle?.info!.mileage ? vehicle.info!.mileage : 'Not Set'}
                  </Typography>
                </Stack>{' '}
                <Stack>
                  <Typography variant="subtitle1">VIN:</Typography>
                  <Typography>
                    {vehicle?.info!.vin ? vehicle.info!.vin : 'Not Set'}
                  </Typography>
                </Stack>
                <DviVehicleUpdateButton
                  vehicleId={vehicle?.id!}
                  mergeCallback={() => window.location.reload()}>
                  Edit
                </DviVehicleUpdateButton>
              </Stack>
            </Grid>
          </Grid>
          <Box>
            <InspectionsPanel
              repairOrderId={id!}
              inspections={inspections}
              isDvi
            />
          </Box>
          <ConcernAndNotes repairOrder={repairOrder} />
          <Box>
            <FormControl size="small" data-testid="RepairOrderStatusSelect">
              <InputLabel>Service Status</InputLabel>
              <Select
                label="Service Status"
                value={statusFilterOption}
                onChange={(event) => {
                  setStatusFilterOption(
                    event.target.value as NewServiceStatus | 'All'
                  )
                }}>
                {[
                  ServiceInfoStatusEnum.Authorized,
                  ServiceInfoStatusEnum.Recommended,
                  ServiceInfoStatusEnum.Deferred,
                  'All'
                ].map((status) => (
                  <MenuItem value={status} key={status}>
                    {status.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {filteredServiceItems &&
            filteredServiceItems.map((serviceItem) => (
              <Paper
                elevation={0}
                sx={
                  repairOrder.entry!.info!.isEstimate
                    ? estimateServiceCardStyle
                    : repairOrderServiceCardStyle[serviceItem.status as string]
                }
                key={serviceItem.id}>
                <ServiceCard
                  key={serviceItem.id}
                  serviceItem={serviceItem}
                  handleOrderCart={() => {}}
                  readOnly={true}
                  showPrice={false}
                  createAuthorizationCallback={() =>
                    setStatusFilterOption(ServiceInfoStatusEnum.Authorized)
                  }
                />
              </Paper>
            ))}
        </Stack>
      )}
    </DviPage>
  )
}

export default DviSingleJob
